import React, { useState, useRef, useEffect } from "react"
import { CollapsableFilter } from "./collapsable_filter"
import { IncludeExcludeTag } from "./include_exclude_tag"

const t = (key: string) => window.i18n.t(key)

interface FoundedYearFilterProps {
  fromValue?: number
  toValue?: number
  includedValues?: string[]
  excludedValues?: string[]
  onChangeFrom: (value: number | undefined) => void
  onChangeTo: (value: number | undefined) => void
  onChangeIncluded?: (values: string[]) => void
  onChangeExcluded?: (values: string[]) => void
  isLocked?: boolean
}

export function FoundedYearFilter({
  fromValue,
  toValue,
  includedValues = [],
  excludedValues = [],
  onChangeFrom,
  onChangeTo,
  onChangeIncluded,
  onChangeExcluded,
  isLocked = false,
}: FoundedYearFilterProps) {
  // Determine initial tab based on values
  const hasIntervalValues = fromValue !== undefined || toValue !== undefined
  const hasSpecificValues = includedValues.length > 0 || excludedValues.length > 0
  const initialTab = hasSpecificValues && !hasIntervalValues ? "specific" : "interval"

  const [activeTab, setActiveTab] = useState<"interval" | "specific">(initialTab)
  const [inputValue, setInputValue] = useState("")

  const currentYear = new Date().getFullYear()
  const hasValues = hasIntervalValues || hasSpecificValues

  // Track display order for specific years
  const allSelectedSet = new Set([...includedValues, ...excludedValues])
  const displayOrderRef = useRef<string[]>([])

  useEffect(() => {
    const currentOrder = displayOrderRef.current
    const keptItems = currentOrder.filter((year) => allSelectedSet.has(year))
    const newItems = [...allSelectedSet].filter((year) => !currentOrder.includes(year))
    displayOrderRef.current = [...keptItems, ...newItems]
  }, [includedValues, excludedValues])

  const orderedSelectedValues = displayOrderRef.current.filter((year) => allSelectedSet.has(year))
  const missingItems = [...allSelectedSet].filter((year) => !orderedSelectedValues.includes(year))
  const allSelectedYears = [...orderedSelectedValues, ...missingItems]

  const handleClear = () => {
    onChangeFrom(undefined)
    onChangeTo(undefined)
    onChangeIncluded?.([])
    onChangeExcluded?.([])
    setInputValue("")
  }

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onChangeFrom(value ? parseInt(value, 10) : undefined)
  }

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onChangeTo(value ? parseInt(value, 10) : undefined)
  }

  const handleAddYear = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault()
      addYear()
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      addYear()
    }
  }

  const addYear = () => {
    const year = inputValue.trim()
    const yearNum = parseInt(year, 10)
    if (yearNum >= 1000 && yearNum <= currentYear && !allSelectedSet.has(year)) {
      onChangeIncluded?.([...includedValues, year])
    }
    setInputValue("")
  }

  const handleRemoveYear = (year: string) => {
    onChangeIncluded?.(includedValues.filter((v) => v !== year))
    onChangeExcluded?.(excludedValues.filter((v) => v !== year))
  }

  const handleToggleIncludeExclude = (year: string) => {
    if (includedValues.includes(year)) {
      onChangeIncluded?.(includedValues.filter((v) => v !== year))
      onChangeExcluded?.([...excludedValues, year])
    } else {
      onChangeExcluded?.(excludedValues.filter((v) => v !== year))
      onChangeIncluded?.([...includedValues, year])
    }
  }

  const handleTabChange = (tab: "interval" | "specific") => {
    setActiveTab(tab)
  }

  const dataCount = hasIntervalValues ? 1 : allSelectedSet.size

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.founded_year.title")}
      icon="far fa-calendar"
      tooltip={t("js.back.discover.filters.founded_year.tooltip")}
      count={dataCount}
      hasValues={hasValues}
      onClear={handleClear}
      isLocked={isLocked}
      lockedTooltip={t("js.back.discover.filters.founded_year.locked_popover")}
    >
      <nav className="h-pill-tabs h-pill-tabs--sm h-pill-tabs--full mb-2">
        <button
          type="button"
          className={`h-pill-tabs__link${activeTab === "interval" ? " is-active" : ""}`}
          onClick={() => handleTabChange("interval")}
        >
          <span className="h-pill-tabs__name">{t("js.back.discover.filters.founded_year.interval")}</span>
        </button>
        <button
          type="button"
          className={`h-pill-tabs__link${activeTab === "specific" ? " is-active" : ""}`}
          onClick={() => handleTabChange("specific")}
        >
          <span className="h-pill-tabs__name">{t("js.back.discover.filters.founded_year.specific_years")}</span>
        </button>
      </nav>

      {activeTab === "interval" && (
        <div className="signal-custom-dates">
          <div className="form-group">
            <label className="h-label h-label--block" htmlFor="founded-from">
              {t("js.back.discover.filters.from")}
            </label>
            <input
              id="founded-from"
              type="number"
              className="h-input h-input--block"
              value={fromValue ?? ""}
              onChange={handleFromChange}
              min="1000"
              max={currentYear}
            />
          </div>
          <div className="form-group">
            <label className="h-label h-label--block" htmlFor="founded-to">
              {t("js.back.discover.filters.to")}
            </label>
            <input
              id="founded-to"
              type="number"
              className="h-input h-input--block"
              value={toValue ?? ""}
              onChange={handleToChange}
              min="1000"
              max={currentYear}
            />
          </div>
        </div>
      )}

      {activeTab === "specific" && (
        <div>
          <label className="sr-only" htmlFor="founded-year-input">
            {t("js.back.discover.filters.founded_year.title")}
          </label>
          <input
            id="founded-year-input"
            type="number"
            className="h-input h-input--block"
            placeholder={t("js.back.discover.filters.founded_year.year_placeholder")}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleAddYear}
            onBlur={handleBlur}
            min="1000"
            max={currentYear}
          />

          {allSelectedYears.length > 0 && (
            <div className="mt-2">
              {allSelectedYears.map((year) => (
                <IncludeExcludeTag
                  key={year}
                  label={year}
                  isIncluded={includedValues.includes(year)}
                  onToggle={() => handleToggleIncludeExclude(year)}
                  onRemove={() => handleRemoveYear(year)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </CollapsableFilter>
  )
}
